import api from './api';

export interface UploadFileParams {
  workspaceId: string;
  file: File;
  entityType?: string;
  entityId?: string;
  fileType?: string;
  title?: string;
  description?: string;
}

export interface UploadedFile {
  _id: string;
  url: string;
  key: string;
  fileType: string;
  originalName: string;
  title?: string;
  size: number;
  mimeType: string;
  isPublic: boolean;
  purchaserCanAccess?: boolean;
  createdAt: string;
}

export interface UploadFileResponse {
  message: string;
  file: UploadedFile;
}

/**
 * Upload a file to S3 via the API
 */
export const uploadFile = async (params: UploadFileParams): Promise<UploadedFile> => {
  const {
    workspaceId,
    file,
    entityType,
    entityId,
    fileType = 'document',
    title,
    description
  } = params;

  const formData = new FormData();
  formData.append('file', file);

  if (entityType) formData.append('entityType', entityType);
  if (entityId) formData.append('entityId', entityId);
  if (fileType) formData.append('fileType', fileType);
  if (title) formData.append('title', title);
  if (description) formData.append('description', description);

  const response = await api.post<UploadFileResponse>(
    `/workspaces/${workspaceId}/upload/file`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data.file;
};

/**
 * Delete a file from S3 and database
 */
export const deleteFile = async (workspaceId: string, fileId: string): Promise<void> => {

  await api.delete(
    `/workspaces/${workspaceId}/upload/file/${fileId}`,
    {
      headers: {
      }
    }
  );
};

/**
 * Get files for an entity
 */
export const getFiles = async (
  workspaceId: string,
  filters?: {
    entityType?: string;
    entityId?: string;
    fileType?: string;
  }
): Promise<UploadedFile[]> => {

  const params = new URLSearchParams();
  if (filters?.entityType) params.append('entityType', filters.entityType);
  if (filters?.entityId) params.append('entityId', filters.entityId);
  if (filters?.fileType) params.append('fileType', filters.fileType);

  const response = await api.get(
    `/workspaces/${workspaceId}/files?${params.toString()}`,
    {
      headers: {
      }
    }
  );

  return response.data.files;
};

/**
 * Get presigned URL for a file
 */
export const getFileUrl = async (
  workspaceId: string,
  fileId: string,
  expiresIn?: number
): Promise<{ url: string; fileId: string; originalName: string; expiresIn: number }> => {

  const params = new URLSearchParams();
  if (expiresIn) params.append('expiresIn', expiresIn.toString());

  const response = await api.get(
    `/workspaces/${workspaceId}/files/${fileId}/url?${params.toString()}`,
    {
      headers: {
      }
    }
  );

  return response.data;
};

/**
 * Update a file (title and/or change access)
 */
export const updateFile = async (
  workspaceId: string,
  fileId: string,
  data: { title: string; purchaserCanAccess?: boolean }
): Promise<UploadedFile> => {
  const response = await api.patch(
    `/workspaces/${workspaceId}/files/${fileId}/rename`,
    data
  );

  return response.data.file;
};
